import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Preloader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const start = Date.now();
    const minDuration = 2000;
    let raf;

    const tick = () => {
      const elapsed = Date.now() - start;
      const raw = Math.min(elapsed / minDuration, 1);
      // Ease out quad for smooth progress
      const eased = 1 - (1 - raw) * (1 - raw);
      setProgress(Math.floor(eased * 100));

      if (raw < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setIsExiting(true);
        setTimeout(() => onComplete?.(), 800);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!isExiting ? (
        <motion.div
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center"
          style={{ background: '#07070A' }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        >
          {/* Logo reveal */}
          <motion.div className="mb-8 overflow-hidden">
            <motion.img
              src="/proxima-labs-logo.png"
              alt="Próxima Labs"
              className="w-64 sm:w-80"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            />
          </motion.div>

          {/* Progress bar */}
          <div className="w-48 h-[2px] bg-white/10 rounded-full overflow-hidden mb-4">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          {/* Counter */}
          <motion.span
            className="font-mono text-sm text-gray-500 tracking-wider"
            style={{ fontFamily: "'Geist Mono', monospace", fontFeatureSettings: "'tnum'" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {progress}%
          </motion.span>
        </motion.div>
      ) : (
        /* Split exit animation */
        <>
          <motion.div
            className="fixed top-0 left-0 right-0 h-1/2 z-[10000]"
            style={{ background: '#07070A' }}
            initial={{ y: 0 }}
            animate={{ y: '-100%' }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 h-1/2 z-[10000]"
            style={{ background: '#07070A' }}
            initial={{ y: 0 }}
            animate={{ y: '100%' }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
